import { requestSearchSignatoryCertificate } from "@/state/signatory/SignatoryEffects";
import { setTransactionSignatories } from "@/state/transactions/CurrentTransactionEvents";
import { useCurrentTransaction } from "@/state/transactions/CurrentTransactionHook";
import { ActiveUser, UserOffice } from "@/type/core/auth/auth.types";
import { Member } from "@/type/core/offices/offices.types";
import { InvitationMode } from "@/type/core/transaction/transaction.types";
import { ArrayHelper } from "@/utils/helpers/array-helper";
import { PhoneHelper } from "@/utils/helpers/phone-helper";
import { TransactionHelper } from "@/utils/helpers/transaction-helper";
import { requestSearchContactsForOffice } from "@state/contact/ContactsEffects";
import { requestSearchExpertsForOffice } from "@state/offices/OfficesEffects";
import { Contact, Signatory } from "@type/core/dashboard/dashboard.types";
import { toastError } from "@utils/helpers/toast-helper";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface SignatoriesSectionHookProps {
  officeId: string | undefined;
  selectedOffice: UserOffice | undefined;
  signatories: Signatory[];
  setSignatories: (signatories: Signatory[]) => void;
  experts: Member[];
  contacts: Contact[];
  submit: () => void;
  originEditor?: boolean;
  signatoryError?: string;
  setSignatoryError: (error?: string) => void;
  invitationMode: InvitationMode;
}

const useSignatoriesSection = (
  me?: ActiveUser,
): SignatoriesSectionHookProps => {
  const { t } = useTranslation();
  const currentTransactionStore = useCurrentTransaction();
  const transactionForm = currentTransactionStore.transactionForm;
  const currentTransactionSignatories = currentTransactionStore.signatories;

  const officeId = transactionForm.office;
  const invitationMode = transactionForm.invitationMode ?? "sequential";
  const offices = me?.offices;
  const selectedOffice = offices?.find((office) => office.id === officeId);

  /* States */
  const [previousSignatories, setPreviousSignatories] = useState<Signatory[]>(
    currentTransactionSignatories,
  );
  // Set des membres du cabinet à ajouter
  const [experts, setExperts] = useState<Member[]>([]);
  // Set de la base de contact du cabinet à ajouter
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [signatoryError, setSignatoryError] = useState<string>();
  /* States */

  useEffect(() => {
    void requestSearchExpertsForOffice({ id: officeId ?? "" });
  }, [officeId]);

  useEffect(() => {
    return requestSearchExpertsForOffice.done.watch(({ result }) => {
      if (result.ok && result.data) {
        setExperts(result.data.contacts || []);
      } else {
        toastError(t("error.toast.expertsLoading"));
      }
    });
  });

  useEffect(() => {
    if (officeId) {
      void requestSearchContactsForOffice({ id: officeId });
    }
  }, [officeId]);

  useEffect(() => {
    return requestSearchContactsForOffice.done.watch(({ result }) => {
      if (result.ok && result.data) {
        setContacts(result.data.contacts || []);
      } else {
        toastError(t("error.toast.contactLoading"));
      }
    });
  });

  const setSignatoriesWithRequestCertificate = async (
    signatoriesWithoutCertificate: Signatory[],
  ) => {
    const processedSignatories = [...signatoriesWithoutCertificate];
    for (const signatory of ArrayHelper.getNewElementsOnly(
      previousSignatories,
      signatoriesWithoutCertificate,
    )) {
      const result = await requestSearchSignatoryCertificate({
        dto: {
          mail: encodeURIComponent(signatory.email),
          phone: encodeURIComponent(signatory.phone ?? ""),
          firstname: signatory.firstname,
          lastname: signatory.lastname,
        },
      });

      if (result.ok) {
        if (result.data) {
          const newSignatory: Signatory = {
            ...signatory,
            certificate: result.data.certificate,
          };
          const indexOfTheSignatoryToModify = processedSignatories.findIndex(
            (s) => s.email === signatory.email,
          );
          if (indexOfTheSignatoryToModify !== -1) {
            processedSignatories[indexOfTheSignatoryToModify] = {
              ...newSignatory,
              phone: PhoneHelper.removePhoneWhitespace(newSignatory.phone),
            };
          }
        }
      } else {
        if (result.data) {
          setSignatoryError(result.data?.certificate);
        } else {
          setSignatoryError(
            t("error.toast.signatoryAdd", { email: signatory.email }),
          );
        }
        return;
      }
    }
    setPreviousSignatories(processedSignatories);
    setTransactionSignatories(processedSignatories);
  };

  const submit = () => {
    if (officeId) {
      TransactionHelper.retrieveTransactionSignatoriesPicturesAndCertificate(
        currentTransactionSignatories,
        currentTransactionStore.signatures,
        officeId,
      );
    }
  };

  return {
    officeId,
    selectedOffice,
    signatories: currentTransactionSignatories,
    setSignatories: (s) => void setSignatoriesWithRequestCertificate(s),
    experts,
    contacts,
    submit,
    originEditor: currentTransactionStore.transactionDetail?.originEditor,
    signatoryError,
    setSignatoryError,
    invitationMode,
  };
};

export default useSignatoriesSection;
